// src/components/users/Table/UsersTableRow.jsx
import React from 'react';
import {
    TableRow,
    TableCell,
    Checkbox,
    Avatar,
    Chip,
    Stack,
    IconButton,
    Tooltip,
    useTheme,
    Box,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';

const UsersTableRow = ({
    user,
    index,
    isArabic,
    isDark,
    page,
    perPage,
    order,
    totalItems,
    selectedIds,
    onRowCheckboxClick,
    onMenuOpen,
}) => {
    const serial =
        order === 'asc'
            ? (page - 1) * perPage + index + 1
            : (page - 1) * perPage + (totalItems - index);

    const isOdd = index % 2 === 0;
    const isSelected = selectedIds.includes(user?.id);
    const MAX_VISIBLE_ROLES = 3;
    const theme = useTheme();
    return (
        <TableRow
            key={user?.id}
            hover
            role="checkbox"
            aria-checked={isSelected}
            selected={isSelected}
            sx={{
                cursor: 'pointer',
                '&:last-child td, &:last-child th': { border: 0 },
                backgroundColor: isDark
                    ? isOdd
                        ? '#020617'
                        : '#050816'
                    : isOdd
                        ? '#F2F7FF'
                        : '#FFFFFF',
                '&:hover': {
                    backgroundColor: isDark ? '#111827' : '#E3F2FD',
                },
                transition: 'background-color 0.15s ease',
            }}
        >
            {/* تشيك بوكس الصف */}
            <TableCell padding="checkbox">
                <Checkbox
                    color="primary"
                    checked={isSelected}
                    onChange={(e) => onRowCheckboxClick(e, user?.id)}
                    inputProps={{ 'aria-labelledby': `user-${user?.id}` }}
                />
            </TableCell>
            <TableCell>{serial}</TableCell>
            <TableCell>
                <Avatar
                    alt={user?.name}
                    src={user?.image?.thumb || user?.image?.url || ''}
                    sx={{ width: 32, height: 32 }}
                >
                    {user?.name?.charAt(0)?.toUpperCase()}
                </Avatar>
            </TableCell>
            <TableCell id={`user-${user?.id}`}>{user?.name}</TableCell>
            <TableCell>{user?.user_name}</TableCell>
            <TableCell>{user?.email}</TableCell>
            <TableCell>{user?.phone}</TableCell>
            <TableCell>
                <Chip
                    label={
                        user?.is_admin
                            ? isArabic
                                ? 'مدير'
                                : 'Admin'
                            : isArabic
                                ? 'مستخدم'
                                : 'User'
                    }
                    size="small"
                    color={user?.is_admin ? 'primary' : 'default'}
                    variant={user?.is_admin ? 'filled' : 'outlined'}
                />
            </TableCell>
            <TableCell>
                <Chip
                    label={
                        user?.status
                            ? isArabic
                                ? 'مفعّل'
                                : 'Active'
                            : isArabic
                                ? 'موقوف'
                                : 'Inactive'
                    }
                    size="small"
                    color={user?.status ? 'success' : 'error'}
                    variant="outlined"
                />
            </TableCell>

            <TableCell>
                <Stack direction="row" spacing={0.5} rowGap={1} flexWrap="wrap">
                    {user?.roles?.slice(0, MAX_VISIBLE_ROLES).map((role) => (
                        <Chip
                            key={role?.id}
                            label={role?.name}
                            size="small"
                            sx={{ mb: 0.3, maxWidth: 120 }}
                        />
                    ))}
                    {user?.roles && user.roles.length > MAX_VISIBLE_ROLES && (
                        <Tooltip
                            arrow
                            placement="top"
                            componentsProps={{
                                tooltip: {
                                    sx: {
                                        bgcolor: isDark ? '#020617' : '#FFFFFF',
                                        color: isDark ? '#E5E7EB' : '#111827',
                                        boxShadow: theme.shadows[3],
                                        border: `1px solid ${isDark ? 'rgba(148,163,184,0.4)' : 'rgba(148,163,184,0.6)'
                                            }`,
                                        px: 1.2,
                                        py: 0.8,
                                        borderRadius: 1.5,
                                        maxWidth: 260,
                                    },
                                },
                                arrow: {
                                    sx: {
                                        color: isDark ? '#020617' : '#FFFFFF',
                                    },
                                },
                            }}
                            title={
                                <Box>
                                    <Box
                                        sx={{
                                            mb: 0.5,
                                            fontSize: 11,
                                            fontWeight: 600,
                                            opacity: 0.8,
                                        }}
                                    >
                                        {isArabic ? 'جميع الأدوار' : 'All roles'}
                                    </Box>
                                    <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                        {user.roles.map((role) => (
                                            <Chip
                                                key={role?.id}
                                                label={role?.name}
                                                size="small"
                                                variant="outlined"
                                                sx={{
                                                    mb: 0.3,
                                                    fontSize: 11,
                                                    height: 22,
                                                    borderColor: isDark
                                                        ? 'rgba(148,163,184,0.6)'
                                                        : 'rgba(59,130,246,0.4)',
                                                }}
                                            />
                                        ))}
                                    </Stack>
                                </Box>
                            }
                        >
                            <Chip
                                size="small"
                                color="primary"
                                variant="outlined"
                                label={`+${user.roles.length - MAX_VISIBLE_ROLES}`}
                                sx={{
                                    mb: 0.3,
                                    fontSize: 11,
                                    height: 22,
                                    bgcolor: isDark ? 'rgba(37,99,235,0.12)' : 'rgba(37,99,235,0.04)',
                                    borderColor: isDark
                                        ? 'rgba(96,165,250,0.6)'
                                        : 'rgba(37,99,235,0.6)',
                                }}
                            />
                        </Tooltip>
                    )}
                </Stack>
            </TableCell>
            <TableCell align="center">
                <IconButton
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        onMenuOpen(e, user);
                    }}
                    sx={{
                        color: isDark ? '#E5E7EB' : '#1F2937',
                    }}
                >
                    <MoreVertIcon fontSize="small" />
                </IconButton>
            </TableCell>
        </TableRow>
    );
};

export default UsersTableRow;
